import type { TaskOutputWithNewlyCreated } from "@orsane/schema/tasks";
import { useQueryClient } from "@tanstack/react-query";
import Autolinker from "autolinker";
import { produce } from "immer";
import { MessageCircle, PanelRightOpen, ReceiptText } from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTaskNavigation } from "@/hooks/useTaskNavigation";
import { cn } from "@/lib/utils";
import {
    useCompleteTaskMutation,
    useInsertTaskMutation,
    useUpdateTaskPropertiesMutation,
} from "@/mutations/task-mutations";
import { useTasksQueryOptions } from "@/queries/task-queries";
import { useAddTask } from "./AddTaskContext";
import { CompletedCheckbox } from "./CompletedCheckbox";
import { useNewTaskIndent } from "./NewTaskIndentContext";
import { useTaskQueryParams } from "./TaskQueryParamsContext";
import { useIsEditingTask, useTaskSelectionActions } from "./TaskSelectionContext";

// Memoized autolinker configuration to avoid recreating on every render
const AUTOLINKER_CONFIG = {
    className: "underline",
    stripPrefix: false,
    stripTrailingSlash: false,
    newWindow: true,
    mention: false,
    hashtag: false,
} as const;

export const TitleCell = memo(function TitleCell({ task }: { task: TaskOutputWithNewlyCreated }) {
    const taskQueryParams = useTaskQueryParams();
    const tasksQueryOptions = useTasksQueryOptions(taskQueryParams);
    const [isEditing, setIsEditing] = useState(task.id < 0);
    const [title, setTitle] = useState(task.title);
    const inputRef = useRef<HTMLInputElement>(null);
    const updatePropertiesMutation = useUpdateTaskPropertiesMutation();
    const insertMutation = useInsertTaskMutation();
    const { mutate: mutateCompleteTask } = useCompleteTaskMutation();
    const queryClient = useQueryClient();
    const isNew = task.id < 0; // Temporary tasks have negative IDs
    const isDone = task.taskStatus!.isCompleted;
    const { openTask: navigateToTask } = useTaskNavigation();
    const isMouseOverRef = useRef(false);
    const savedViaEnterRef = useRef(false);
    const addTask = useAddTask();
    const newTaskIndent = useNewTaskIndent();
    const { setSelectionByIds, setEditingTaskId } = useTaskSelectionActions();
    // Only this cell's own editing state, so "edit title" on one row doesn't
    // re-render every other title cell in the table.
    const isEditRequested = useIsEditingTask(task.id);

    // Memoize the autolinked HTML to avoid expensive computation on every render
    const linkedTitle = useMemo(() => {
        return Autolinker.link(title, AUTOLINKER_CONFIG);
    }, [title]);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isEditing]);

    useEffect(() => {
        setTitle(task.title);
    }, [task.title]);

    useEffect(() => {
        if (isNew && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isNew]);

    useEffect(() => {
        if (isEditRequested) {
            setIsEditing(true);
            setEditingTaskId(null);
        }
    }, [isEditRequested, setEditingTaskId]);

    const save = useCallback(async () => {
        const triggeredByEnter = savedViaEnterRef.current;
        savedViaEnterRef.current = false;

        const trimmedTitle = title.trim();
        if (trimmedTitle !== task.title && trimmedTitle !== "") {
            // if this thask has a title, we proceed
            if (task.isNewlyCreated) {
                // new task, insert it
                const dto = { ...task, title: trimmedTitle };
                await insertMutation.mutateAsync(dto);

                // Auto-create next task row when submitted via Enter, at the same nesting level
                if (triggeredByEnter && addTask) {
                    addTask(task._groupValue ?? null, task.parentTask);
                }
            } else {
                // existing task, update the title
                const dto = { id: task.id, updates: { title: trimmedTitle } };
                updatePropertiesMutation.mutate(dto);
            }
        } else {
            // this is an empty line, remove it if it is a new task
            if (isNew) {
                removeTempTask(task.id);
            }
        }
        setIsEditing(false);
    }, [title, task, insertMutation, updatePropertiesMutation, addTask]);

    const handleBlur = useCallback(() => {
        save();
    }, [save]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            e.stopPropagation();
            if (e.key === "Tab" && isNew) {
                // Indent / de-indent the still-unsaved task, mirroring the Tab hotkey that
                // works on selected rows. preventDefault stops the input from tabbing focus
                // away so the user can keep typing the title after (un)nesting.
                e.preventDefault();
                if (e.shiftKey) {
                    newTaskIndent?.outdent(task.id);
                } else {
                    newTaskIndent?.indent(task.id);
                }
            } else if (e.key === "Enter") {
                savedViaEnterRef.current = true;
                save();
            } else if (e.key === "Escape") {
                setTitle(task.title);
                setIsEditing(false);

                // if it is a new task, we need to remove it
                if (isNew) {
                    removeTempTask(task.id);
                }
            }
        },
        [save, task.title, isNew, task.id, newTaskIndent, queryClient, tasksQueryOptions.queryKey]
    );

    const removeTempTask = (negativeIdToRemove: number) => {
        const data = queryClient.getQueryData(tasksQueryOptions.queryKey);
        if (data) {
            const newData = produce(data, (draft: TaskOutputWithNewlyCreated[]) => {
                return draft.filter((task: TaskOutputWithNewlyCreated) => task.id !== negativeIdToRemove);
            });
            queryClient.setQueryData(tasksQueryOptions.queryKey, newData);
        }
    };

    const openTask = useCallback(
        (id: number, tab?: "activity" | "description") => {
            // Use optimized task navigation for better performance
            navigateToTask(id, tab || undefined);
        },
        [navigateToTask]
    );

    const handleCompleteTask = useCallback(
        (newChecked: boolean) => {
            mutateCompleteTask({
                id: task.id,
                isCompleted: newChecked,
            });
        },
        [mutateCompleteTask, task.id]
    );

    const handleCellDoubleClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            openTask(task.id);
        },
        [openTask, task.id]
    );

    const handleTitleDoubleClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        setIsEditing(true);
    }, []);

    const handleEditClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            openTask(task.id);
            setSelectionByIds({ [task.id]: true });
        },
        [openTask, task.id, setSelectionByIds]
    );

    const handleActivityClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            openTask(task.id, "activity");
        },
        [openTask, task.id]
    );

    const handleTitleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setTitle(e.target.value);
    }, []);

    const handleMouseMove = useCallback(() => {
        isMouseOverRef.current = true;
    }, []);
    const handleMouseEnter = useCallback(() => {
        isMouseOverRef.current = true;
    }, []);

    const handleMouseLeave = useCallback(() => {
        isMouseOverRef.current = false;
    }, []);

    return (
        // biome-ignore lint/a11y/noStaticElementInteractions: because
        <div
            className={cn(
                "px-3 pl-5 flex items-center gap-2 col-start-1 h-full rounded-md relative group/title-cell w-full",
                {
                    "ring ring-neutral-600 ring-inset bg-white z-50": isEditing,
                }
            )}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onMouseEnter={handleMouseEnter}
            onDoubleClick={handleCellDoubleClick}
        >
            <CompletedCheckbox checked={isDone} onChange={handleCompleteTask} />
            {!isEditing && (
                <div className="appearance-none border-none w-full h-full flex items-center flex-1 pr-14 gap-1.5">
                    {/* biome-ignore lint/a11y/noStaticElementInteractions: double-click enters inline edit */}
                    <div
                        className="truncate mr-2 cursor-text"
                        onDoubleClick={handleTitleDoubleClick}
                        dangerouslySetInnerHTML={{
                            __html: linkedTitle,
                        }}
                    />
                    {task.hasDescription && (
                        <button
                            type="button"
                            className="text-zinc-400/70 hover:text-orsane-primary appearance-none shrink-0 cursor-pointer"
                            onClick={handleEditClick}
                        >
                            <ReceiptText className="size-3.5" />
                        </button>
                    )}
                    {task.hasActivity && (
                        <button
                            type="button"
                            className="text-zinc-400/70 hover:text-orsane-primary appearance-none shrink-0 cursor-pointer"
                            onClick={handleActivityClick}
                        >
                            <MessageCircle className="size-3.5" />
                        </button>
                    )}
                </div>
            )}
            {isEditing && (
                <input
                    ref={inputRef}
                    type="text"
                    value={title}
                    onChange={handleTitleChange}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    // Stop the pointer-down from reaching the row's dnd-kit drag listeners so
                    // dragging to select text isn't hijacked into a row drag.
                    onPointerDown={(e) => e.stopPropagation()}
                    // Stop double-click from bubbling to the cell handler (which opens the
                    // detail panel) so it does the default browser word-selection instead.
                    onDoubleClick={(e) => e.stopPropagation()}
                    className={cn("appearance-none w-full h-full border-none focus:outline-none flex-1 select-text")}
                    placeholder="Enter task title"
                />
            )}
            {!isEditing && (
                <button
                    type="button"
                    disabled={!!task.isNewlyCreated}
                    className={"title-cell-pencil"}
                    onClick={handleEditClick}
                >
                    <PanelRightOpen className="size-4" />
                </button>
            )}
        </div>
    );
});
